import React, {useState} from 'react';
import {Alert, Box, Button, Grid, TextField} from '@mui/material';
import {useMutation} from '@apollo/client';
import {useSnackbar} from 'notistack';
import {ScrollableModal} from '../../components/Common/ScrollableModal';
import {Panel} from '../../components/Panel/Panel';
import {VulnerabilityFragment} from '../../schema-types/VulnerabilityFragment';
import {
  UpsertVulnerability,
  UpsertVulnerabilityVariables,
} from '../../schema-types/UpsertVulnerability';
import {getErrorMessage} from '../../utils/getErrorMessage';
import validateUUID from '../../utils/validateUUID';
import {UPSERT_VULNERABILITY} from './vulnerabilityQueries';
import {VulnerabilityOptionLists} from './useVulnerabilityOptions';
import {toIsoDate, todayIsoDate} from './vulnerabilityFormatting';

export type VulnerabilityModalContact = {id: string; simplifiedName: string | null};

type Props = {
  open: boolean;
  onClose: () => void;
  /** Called after Curo accepted the record, before the modal closes. */
  onSaved: () => void;
  accountId: string;
  contact: VulnerabilityModalContact;
  /** The row being edited; omit to add a new vulnerability. */
  existing?: VulnerabilityFragment | null;
  options: VulnerabilityOptionLists;
};

/**
 * SPP add / edit dialog for one vulnerability. Saving writes to Curo in the same
 * request, so a Curo rejection is shown here and nothing is left half-saved.
 */
export const VulnerabilityModal: React.FC<Props> = ({
  open,
  onClose,
  onSaved,
  accountId,
  contact,
  existing = null,
  options,
}) => {
  const isEdit = Boolean(existing);
  const [vulnerabilityType, setVulnerabilityType] = useState(
    existing?.vulnerabilityType ?? ''
  );
  const [serviceLevel, setServiceLevel] = useState(existing?.serviceLevel ?? '');
  const [startDate, setStartDate] = useState(
    existing?.startDate ? toIsoDate(existing.startDate) : todayIsoDate()
  );
  const [endDate, setEndDate] = useState(toIsoDate(existing?.endDate));
  const [errors, setErrors] = useState<string[]>([]);
  const {enqueueSnackbar} = useSnackbar();
  const [upsertVulnerability, {loading}] = useMutation<
    UpsertVulnerability,
    UpsertVulnerabilityVariables
  >(UPSERT_VULNERABILITY);

  const canSubmit = Boolean(vulnerabilityType && serviceLevel && startDate) && !loading;

  const submit = async () => {
    setErrors([]);
    const variables: UpsertVulnerabilityVariables = {
      vulnerabilityType,
      serviceLevel,
      startDate,
      endDate: endDate || null,
    };
    if (existing) {
      variables.vulnerabilityId = existing.id;
    } else {
      variables.contactId = validateUUID(contact.id);
      variables.accountId = validateUUID(accountId);
    }
    try {
      const {data} = await upsertVulnerability({variables});
      const result = data?.upsertVulnerability;
      if (!result?.ok) {
        setErrors(
          result?.errors?.length ? result.errors : ['Could not save the vulnerability.']
        );
        return;
      }
      enqueueSnackbar(
        isEdit ? 'Vulnerability updated in Curo.' : 'Vulnerability added to Curo.',
        {variant: 'success'}
      );
      onSaved();
      onClose();
    } catch (error) {
      setErrors([getErrorMessage(error, 'Could not reach the server.')]);
    }
  };

  const contactName = contact.simplifiedName || 'contact';

  return (
    <ScrollableModal size="small" autoHeight open={open} onClose={onClose}>
      <Panel title={`${isEdit ? 'Edit' : 'Add'} vulnerability for ${contactName}`}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              id="vulnerability-type"
              select
              SelectProps={{native: true}}
              label="Vulnerability"
              required
              fullWidth
              size="small"
              InputLabelProps={{shrink: true}}
              value={vulnerabilityType}
              onChange={(event) => setVulnerabilityType(event.target.value)}
            >
              <option value="">Select a vulnerability</option>
              {options.vulnerabilityTypes.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="vulnerability-service-level"
              select
              SelectProps={{native: true}}
              label="Service level"
              required
              fullWidth
              size="small"
              InputLabelProps={{shrink: true}}
              value={serviceLevel}
              onChange={(event) => setServiceLevel(event.target.value)}
            >
              <option value="">Select a service level</option>
              {options.serviceLevels.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="vulnerability-start-date"
              type="date"
              label="Start date"
              required
              fullWidth
              size="small"
              InputLabelProps={{shrink: true}}
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="vulnerability-end-date"
              type="date"
              label="End date"
              fullWidth
              size="small"
              InputLabelProps={{shrink: true}}
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </Grid>
        </Grid>
        {errors.length > 0 ? (
          <Alert severity="error" sx={{mt: 2}}>
            {errors.join(' ')}
          </Alert>
        ) : null}
        <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
          <Button variant="text" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!canSubmit}
            onClick={submit}
          >
            {isEdit ? 'Save' : 'Add'}
          </Button>
        </Box>
      </Panel>
    </ScrollableModal>
  );
};
